import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { Box } from '~/eds/src/components/Box/Box';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingShimmer } from '~/eds/src/components/LoadingShimmer/LoadingShimmer';
import { Text } from '~/eds/src/components/Text/Text';
import { Highlight } from '~/features/document-viewer/types/highlight';
import { HighlightSources } from '~/features/document/HighlightSources';
import { slices } from '~/redux/slices';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import { INFO_LABEL_FLEX, INFO_SPACING } from '../../constants';

interface Props {
  documentVersionId: Nullable<number>;
  highlights?: Highlight[];
  isLoading?: boolean;
}

export const ReviewHighlightSources = ({
  documentVersionId,
  highlights,
  isLoading,
}: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [activeHighlightSourceId, setActiveHighlightSourceId] =
    useState<Uuid>();

  const handleSetActiveHighlightSourceId = (highlight: Highlight) => {
    setActiveHighlightSourceId(highlight.id);
    dispatch(
      slices.xRay.actions.setDocumentViewer({
        activeHighlightId: highlight.id,
        documentVersionId,
        highlights: [highlight],
        invalidateActiveHighlightId: uuid(),
      }),
    );
  };

  return (
    <Layout align="center" spacing={INFO_SPACING}>
      <Text flex={INFO_LABEL_FLEX} variant="body-bold">
        {intl.formatMessage({
          id: 'ReviewHighlightSources.sources',
          defaultMessage: 'Source(s)',
          description: 'Label for highlight sources section',
        })}
      </Text>
      {isLoading ? (
        <LoadingShimmer height="14px" />
      ) : (
        <Box py={INFO_SPACING}>
          <HighlightSources
            highlights={highlights ?? []}
            activeHighlightId={activeHighlightSourceId}
            onHighlightSourceClick={handleSetActiveHighlightSourceId}
          />
        </Box>
      )}
    </Layout>
  );
};
